// IE Hub - DocumentPage Screen
// Document upload interface with drag & drop functionality

import { Box, Typography, Paper, Button } from "../../../ui";
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DashboardLayout from "@/components/DashboardLayout";

import { BudgetService, DocumentsPageService } from "@/shared/api/services";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import {
  ApiResponse,
  CustomerDeclarationEntry,
  UpdateDocumentRequest,
} from "@/shared/api/types";
import { Modal } from "@mui/material";
import { ThemeColours } from "@/clients/acme/config/colours";
import { CircularProgress } from "@ui";
import { useAlert } from "@/app/providers/AlertProvider";
import { trackMetaEvent } from "@/shared/tracking/metaTracking";
import DocumentsList from "@/components/DocumentsList";
import {
  ConfirmDialogHost,
  openConfirmDialog,
} from "@/components/ConfirmDialog";
import SmartSuggestionsListUU from "@/components/SmartSuggestionsListUU";
import { inferMimeType, MAX_DOCS_WARNING } from "@/shared/lib/utils/documents";
import { declarationEndpoints } from "@/shared/api/endpoints";
import { httpGet } from "@/shared/api/http";
import { isAllowedEntrySource } from "@/constants/documents";

// Document type
export interface UploadedDocument {
  id: string;
  name: string;
  documentName: string;
  size: string;
  uploadDate: string;
  progress: number;
  status: string;
  apiObject: UpdateDocumentRequest;
  file?: File;
  showNotes?: boolean;
  entrySource?: string;
}

function DocumentPage(): JSX.Element {
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const [openPreview, setOpenPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewFileType, setPreviewFileType] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | null>(null);
  const [raw, setRaw] = useState<CustomerDeclarationEntry>();
  const [loadedDeclarationData, setLoadedDeclarationData] = useState(false);

  const [isUploading, setIsUploading] = useState(false);

  const [modifiedDocuments, setModifiedDocuments] = useState<
    Record<string, string>
  >({});
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [suggestionsDocumentsCount, setSuggestionsDocumentsCount] =
    useState<number>(0);

  const handleNameChange = (doc: UploadedDocument, newName: string) => {
    setDocuments((prev) =>
      prev.map((d) => (d.id === doc.id ? { ...d, documentName: newName } : d))
    );

    // Track modified documents
    setModifiedDocuments((prev) => ({
      ...prev,
      [doc.id]: newName,
    }));
  };

  const handleOpenPreview = async (doc: UploadedDocument) => {
    try {
      setLoadingPreview(true);
      setOpenPreview(true);
      setPreviewFileName(doc.name);


      const data = await DocumentsPageService.downloadCustomerDocument(
        doc.apiObject.documentId
      );

      const blob =
        data instanceof Blob
          ? new Blob([data], { type: data.type || inferMimeType(doc.name) })
          : new Blob([data as unknown as BlobPart], {
              type: inferMimeType(doc.name),
            });

      const blobUrl = URL.createObjectURL(blob);

      console.log(inferMimeType(doc.name), blobUrl, data, data instanceof Blob);

      setPreviewBlobUrl(blobUrl);
      setPreviewFileType(blob.type || inferMimeType(doc.name));
    } catch (err) {
      console.error("Preview failed:", err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setLoadingPreview(false);
    setOpenPreview(false);
  };

  const modifiedDocumentsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    modifiedDocumentsRef.current = modifiedDocuments;
  }, [modifiedDocuments]);

  const documentsRef = useRef<UploadedDocument[]>([]);

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const saveModifiedDocuments = async (onComplete?: () => void) => {
    const ids = Object.keys(modifiedDocumentsRef.current);

    for (const id of ids) {
      const doc = documentsRef.current.find((d) => d.id == id);
      if (!doc) continue;

      const newName = modifiedDocumentsRef.current[id];
      if (!newName) continue;

      const updatedPayload: UpdateDocumentRequest = {
        ...doc.apiObject,
        documentName: newName,
      };

      await DocumentsPageService.updateCustomerDocument(updatedPayload);
    }

    ids.length > 0 &&
      showAlert({
        severity: "success",
        message: "All changes saved successfully.",
        autoHide: true,
        duration: 2000,
      });

    if (onComplete) onComplete();

    setModifiedDocuments({});
  };

  // Handle global "leave document and navigate" requests (e.g. from Header or BottomTabBar navigation)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ targetPath?: string }>;
      const targetPath = custom.detail?.targetPath;
      if (!targetPath) return;

      void (async () => {
        try {
          await saveModifiedDocuments();
        } catch (e) {
          showAlert({
            severity: "error",
            message: "Failed to save some changes.",
            autoHide: true,
            duration: 3000,
          });
        } finally {
          navigate(targetPath);
        }
      })();
    };

    window.addEventListener(
      "iehub:documents-before-leave",
      handler as EventListener
    );
    return () => {
      window.removeEventListener(
        "iehub:documents-before-leave",
        handler as EventListener
      );
    };
  }, [navigate]);

  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async (): Promise<UploadedDocument[]> => {
      let resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        showAlert({
          severity: "warning",
          message:
            "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
          autoHide: true,
          duration: 5000,
        });
        return [];
      }

      try {
        const response =
          await DocumentsPageService.getCustomerDocuments(resolvedCustomerId);

        const data = Array.isArray(response) ? response : response?.data || [];

        return data.map((doc: UpdateDocumentRequest) => ({
          id: String(doc.id),
          name: doc.fileName,
          documentName: doc.documentName,
          size: doc.documentSize,
          uploadDate: formatDate(doc.creationDate || ""),
          progress: 0,
          status: "Already Uploaded",
          apiObject: doc,
          showNotes: !!doc.documentName,
        }));
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return [];
      }
    };

    fetchDocuments().then((documentsArray) => {
      const withEntrySource = documentsArray.filter((c) => {
        const entrySource = (
          c.apiObject as UpdateDocumentRequest & { entrySource?: string }
        ).entrySource;
        return entrySource && !isAllowedEntrySource(entrySource);
      });

      setSuggestionsDocumentsCount(withEntrySource.length);
      // Show UU-triggered documents only in the UU section; hide from main list
      // Exception: allow certain entrySources to be shown in the main list
      setDocuments(
        documentsArray.filter((c) => {
          const entrySource = (
            c.apiObject as UpdateDocumentRequest & { entrySource?: string }
          ).entrySource;
          return isAllowedEntrySource(entrySource);
        })
      );
    });
  }, []);

  const fetchIncome = async (customerId: number) => {
    try {
      const resp = await BudgetService.getIncomeData(customerId);
      if (resp?.success && Array.isArray(resp.data) && resp.data.length > 0) {
        return resp.data[0] as unknown as Record<string, unknown>;
      }
    } catch {
      // ignore fetch errors; treat as no data
    }
    return null;
  };

  // UU Smart Suggestions
  useEffect(() => {
    if (!loadedDeclarationData) {
      return;
    }

    const setupSmartSuggestions = async () => {
      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string
      );

      const incomeData = await fetchIncome(customerData.customerId);
      const payload: Record<string, unknown> = incomeData
        ? { ...incomeData }
        : {};

      const triggers = [
        {
          fieldName: "universalCredit",
          apiFieldName: "otherIncludeUniversalCredit",
          headerTitle: "Universal Credit Proof",
          show: false,
        },
        {
          fieldName: "pensionCredit",
          apiFieldName: "pensionCredit",
          headerTitle: "Pension Credit Proof",
          show: false,
        },
        {
          fieldName: "housingBenefitLocalAllowance",
          apiFieldName: "housingBenefitLocalAllowance",
          headerTitle: "Local Housing Allowance Proof",
          show: false,
        },
        {
          fieldName: "childBenefit",
          apiFieldName: "childBenefit",
          headerTitle: "Child Benefit Proof",
          show: false,
        },
        {
          fieldName: "disabilityAllowance",
          apiFieldName: "dlaPipAttendanceAllowance",
          headerTitle: "Disability Benefit Proof",
          show: false,
        },
      ];

      // set showHide option
      for (const t of triggers) {
        if (parseInt((payload[t.apiFieldName] as string) || "0") > 0) {
          t.show = true;
        }
      }

      const circumstanceSmartSuggestionsMap: Record<string, any> = {
        Bereavement: "deceasedOccupier",
        "Care Leaver aged under 25": null,
        "Drop of working hours": "changeInWorkingHours",
        "Marital Split": "maritalSplit",
        Redundancy: "redundancy",
        "Unable to work due to illness": "dropInIncomeDueToIllness",
        Other: null,
      };

      const changeInCircumstances = raw?.potentialChangeInCircumstances;
      if (
        changeInCircumstances &&
        circumstanceSmartSuggestionsMap[changeInCircumstances]
      ) {
        const fieldName =
          circumstanceSmartSuggestionsMap[changeInCircumstances];

        triggers.push({
          fieldName,
          apiFieldName: fieldName,
          headerTitle: "Potential Change in Circumstances Proof",
          show: true,
        });
      }

      if (raw?.hasMedicalConditionAffectingWaterUsage == "Y") {
        triggers.push({
          fieldName: "medicalEvidence",
          apiFieldName: "medicalEvidence",
          headerTitle: "Medical Evidence Proof",
          show: true,
        });
      }

      // Triggers are used internally by SmartSuggestionsListUU component
      // No need to store them in state
    };

    setupSmartSuggestions();
  }, [loadedDeclarationData]);

  // GET declaration data
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    async function getDeclarationData() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclaration(customerData.customerId)
        );

        const data: any = response?.data;

        const res = data[0] || {};

        setRaw(res);
        setLoadedDeclarationData(true);
      } catch (error) {
        femaCapture("fetching_declaration_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationData();
  }, []);

  const totalDocumentsLength = useMemo(() => {
    const total = documents.length + suggestionsDocumentsCount;
    localStorage.setItem("totalDocumentsLength", String(total));
    return total;
  }, [documents, suggestionsDocumentsCount]);

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (isUploading) {
        return;
      }

      if (totalDocumentsLength >= 10) {
        showAlert({
          severity: "warning",
          message: MAX_DOCS_WARNING,
          autoHide: true,
          duration: 5000,
        });
        return;
      }

      let resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        showAlert({
          severity: "warning",
          message:
            "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
          autoHide: true,
          duration: 5000,
        });
        return false;
      }

      // Handle file drop logic here
      const files = Array.from(e.dataTransfer.files);
      if (totalDocumentsLength + files.length > 10) {
        showAlert({
          severity: "warning",
          message:
            "Looks like you’ve hit the max! You can upload up to 10 documents.",
          autoHide: true,
          duration: 5000,
        });
        return;
      }

      const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];

      for (const file of files) {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (!ext) continue;

        if (!allowedExtensions.includes(ext)) {
          showAlert({
            severity: "error",
            message:
              "Invalid file format. You can only upload PDF, PNG, JPG, or JPEG files.",
            duration: 3000,
          });
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          showAlert({
            severity: "error",
            message:
              "The file is too large. Please select a file smaller than 5MB.",
            duration: 3000,
          });
          return;
        }
      }

      setIsUploading(true);
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append("file", file);

          const response = await DocumentsPageService.uploadCustomerDocument(
            resolvedCustomerId,
            formData,
            window.location.href.includes("?ref=cder")
              ? "CDER Group Limited"
              : "",
            ""
          );

          const { id, documentSize, fileName, creationDate, documentName } =
            response;

          setDocuments((prev) => [
            ...prev,
            {
              id: String(id),
              name: fileName,
              documentName,
              size: documentSize,
              uploadDate: formatDate(creationDate),
              progress: 100,
              status: "Uploaded",
              apiObject: response,
              file,
            },
          ]);
        } catch (err) {
          console.error("Upload failed:", err);
        }
      }

      showAlert({
        severity: "success",
        message: `Successfully uploaded ${files.length} document${files.length > 1 ? "s" : ""}`,
        autoHide: true,
        duration: 5000,
      });

      setIsUploading(false);

      return true;
    },
    [documents, isUploading, totalDocumentsLength]
  );

  const handleFileSelect = useCallback(async (): Promise<void> => {
    if (isUploading) {
      return;
    }

    if (totalDocumentsLength >= 10) {
      showAlert({
        severity: "warning",
        message:
          "Looks like you’ve hit the max! You can upload up to 10 documents.",
        autoHide: true,
        duration: 5000,
      });
      return;
    }

    let resolvedCustomerId: string | number | null = await getCustomerId();

    if (!resolvedCustomerId) {
      showAlert({
        severity: "warning",
        message:
          "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
        autoHide: true,
        duration: 5000,
      });
      return;
    }

    // Trigger file input click
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = async (e: Event) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (totalDocumentsLength + files.length > 10) {
        showAlert({
          severity: "warning",
          message:
            "Looks like you've hit the max! You can upload up to 10 documents.",
          autoHide: true,
          duration: 5000,
        });
        return;
      }

      const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];

      for (const file of files) {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (!ext) continue;

        if (!allowedExtensions.includes(ext)) {
          showAlert({
            severity: "error",
            message:
              "Invalid file format. You can only upload PDF, PNG, JPG, or JPEG files.",
            duration: 3000,
          });
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          showAlert({
            severity: "error",
            message:
              "The file is too large. Please select a file smaller than 5MB.",
            duration: 3000,
          });
          return;
        }
      }

      setIsUploading(true);
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append("file", file);

          const response = await DocumentsPageService.uploadCustomerDocument(
            resolvedCustomerId,
            formData,
            window.location.href.includes("?ref=cder")
              ? "CDER Group Limited"
              : "",
            ""
          );

          const { id, documentSize, fileName, creationDate, documentName } =
            response;

          setDocuments((prev) => [
            ...prev,
            {
              id: String(id),
              name: fileName,
              documentName,
              size: documentSize,
              uploadDate: formatDate(creationDate),
              progress: 100,
              status: "Uploaded",
              apiObject: response,
              file,
            },
          ]);
        } catch (err) {
          console.error("Upload failed:", err);
        }
      }

      showAlert({
        severity: "success",
        message: `Successfully uploaded ${files.length} document${files.length > 1 ? "s" : ""}`,
        autoHide: true,
        duration: 5000,
      });

      setIsUploading(false);
    };

    input.click();
  }, [documents, isUploading, totalDocumentsLength]);

  const handleDeleteDocument = async (id: string) => {
    try {
      const yes = await openConfirmDialog({
        title: "Delete Document",
        message: "Are you sure you want to delete this?",
        confirmText: "Delete",
        cancelText: "Cancel",
      });

      if (!yes) {
        return;
      }

      let resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        showAlert({
          severity: "warning",
          message:
            "Missing customerId. Add ?customerId= in URL or ensure you are logged in.",
          autoHide: true,
          duration: 5000,
        });
        return;
      }

      await DocumentsPageService.deleteCustomerDocument(resolvedCustomerId, id);
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    } catch (e) {
      console.log("An error occurred when deleting document with id:", id);
    }
  };

  return (
    <DashboardLayout backgroundColor="#F8F9FA">
      <ConfirmDialogHost />

      {/* Page Title */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: ThemeColours.titleVar1,
          mb: 1,
          fontSize: 28,
        }}
      >
        Upload documents
      </Typography>

      <Typography
        sx={{ color: ThemeColours.subtitleVar1, mb: 2, fontSize: 16 }}
      >
        Would you like to attach any supporting documents?
      </Typography>

      <Typography
        sx={{ color: ThemeColours.subtitleVar1, mb: 2, fontSize: 16 }}
      >
        Has one of your companies asked you to send proof of any of your income
        and or expenditure? If they have you can upload documents up to 5MB in
        size in PDF or image formats.
      </Typography>

      <Typography
        sx={{ color: ThemeColours.subtitleVar1, mb: 2, fontSize: 16 }}
      >
        You can skip this form by clicking Skip if no one asked you to send any
        proof, but it does help your company understand your finances better.
      </Typography>

      <Typography
        sx={{ color: ThemeColours.subtitleVar1, mb: 4, fontSize: 16 }}
      >
        Please note we will not be able to send the uploaded documents to your
        companies through the Freepost service.
      </Typography>

      {/* Upload Area */}
      <Paper
        elevation={0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: isDragging ? "2px solid #0d9488" : "2px dashed #000",
          borderRadius: 2,
          px: 6,
          py: 4,
          mb: 4,
          textAlign: "center",
          backgroundColor: "#fff",
          transition: "all 0.2s ease-in-out",
          cursor: "pointer",
        }}
        onClick={handleFileSelect}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#DCFCE7",
              borderRadius: "50%",
              p: 2.3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 32, color: "#165F54" }} />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "Drag & drop files here",
              fontSize: 20,
              mb: 0.5,
            }}
          >
            Drag & drop files here
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "#4B5563", fontSize: 16, mb: 1 }}
          >
            or tap to browse your device
          </Typography>

          <Button
            variant="contained"
            startIcon={
              <Typography sx={{ fontSize: "20px", lineHeight: 0.8 }}>
                +
              </Typography>
            }
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleFileSelect();
            }}
            sx={{
              backgroundColor: "#165F54",
              color: "white",
              textTransform: "none",
              fontWeight: 400,
              boxShadow: "none",
              px: 4,
              py: 1.75,
              mt: 1,
              fontSize: 16,
              "&:hover": {
                backgroundColor: "#11473fff",
                boxShadow: "none",
              },
            }}
            endIcon={
              isUploading ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            Choose files
          </Button>

          <Typography
            variant="body2"
            sx={{
              color: ThemeColours.subtitleVar1,
              fontSize: 14,
              mt: 2,
            }}
          >
            PDF, JPG, PNG up to 5 MB
          </Typography>
        </Box>
      </Paper>

      <Modal open={openPreview} onClose={handleClosePreview}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 2,
            maxWidth: "90%",
            maxHeight: "90%",
            overflow: "auto",
            textAlign: "center",
          }}
        >
          {loadingPreview ? (
            <Typography>Loading preview...</Typography>
          ) : previewBlobUrl ? (
            (() => {
              const fileType =
                previewFileType || inferMimeType(previewFileName || undefined);

              const isImage =
                fileType.startsWith("image/") ||
                previewFileName?.match(/\.(jpeg|jpg|png|gif|webp)$/i);

              const isPDF =
                fileType === "application/pdf" ||
                previewFileName?.toLowerCase().endsWith(".pdf");

              if (isImage) {
                return (
                  <img
                    src={previewBlobUrl}
                    alt="preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "80vh",
                      borderRadius: 8,
                      display: "block",
                      margin: "0 auto",
                    }}
                  />
                );
              } else if (isPDF) {
                return (
                  <iframe
                    src={previewBlobUrl}
                    title="PDF Preview"
                    style={{
                      width: "80vw",
                      height: "80vh",
                      border: "none",
                      borderRadius: 8,
                    }}
                  />
                );
              } else {
                return (
                  <Box>
                    <Typography mb={2}>
                      File type not supported for inline preview.
                    </Typography>
                    <a
                      href={previewBlobUrl}
                      download={previewFileName || "download"}
                      style={{
                        display: "inline-block",
                        padding: "8px 16px",
                        background: "#0d9488",
                        color: "#fff",
                        borderRadius: 6,
                        textDecoration: "none",
                      }}
                    >
                      Download File
                    </a>
                  </Box>
                );
              }
            })()
          ) : (
            <Typography color="error">Failed to load preview.</Typography>
          )}
        </Box>
      </Modal>

      {/*  UU Smart Suggestions Functionality */}
      {loadedDeclarationData && raw && (
        <SmartSuggestionsListUU
          uuData={raw}
          totalDocumentsLength={totalDocumentsLength}
          setSuggestionsDocumentsCount={setSuggestionsDocumentsCount}
        />
      )}

      {/* Uploaded Documents List */}
      <Box sx={{ pb: 4 }}>
        <DocumentsList
          documents={documents.map((doc) => ({
            id: doc.id,
            name: doc.name,
            size: doc.size,
            uploadDate: doc.uploadDate,
            documentName: doc.documentName,
            showNotes: doc.showNotes ?? false,
            toggleNotes: (id: string | number, value: boolean) => {
              setDocuments((prev) =>
                prev.map((d) =>
                  String(d.id) === String(id) ? { ...d, showNotes: value } : d
                )
              );
            },
          }))}
          onOpen={async (doc) => {
            const uploadedDoc = documents.find((d) => d.id === String(doc.id));
            if (uploadedDoc) {
              await handleOpenPreview(uploadedDoc);
            }
          }}
          onDelete={(id: string | number) => handleDeleteDocument(String(id))}
          onNameChange={(doc, value: string) => {
            const uploadedDoc = documents.find((d) => d.id === String(doc.id));
            if (uploadedDoc) {
              handleNameChange(uploadedDoc, value);
            }
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            mt: 5,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<ArrowBackIcon sx={{ mr: 1, fontSize: 22 }} />}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
              py: 1.3,
              boxShadow: "none",
              width: 200,
              fontSize: 16,
            }}
            onClick={() =>
              saveModifiedDocuments(() => {
                navigate("/budget");
              })
            }
          >
            Back to budget
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="large"
            endIcon={<ArrowForwardIcon sx={{ ml: 2, fontSize: 22 }} />}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
              py: 1.3,
              boxShadow: "none",
              width: 200,
              backgroundColor: "#165F54",
              fontSize: 16,
            }}
            onClick={() =>
              saveModifiedDocuments(() => {
                navigate("/share");
              })
            }
          >
            Share budget
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
}

export default DocumentPage;
